/**
 * 创建 designer-editor 的 editor-next 实例，注册通用 Canvas 图层与设计师业务插件
 */
import { Editor } from '@meitu/saas-editor-core';
import { downloadExtension, type Download } from '@meitu/saas-editor-core/es/editor/extensions/download';
import { templateExtension, type Template } from '@meitu/saas-editor-core/es/editor/extensions/template';
import { wasmExtension, type Wasm } from '@meitu/saas-editor-core/es/editor/extensions/wasm';
import {
    frameworkHotkeyPlugin,
    layerLoadingPlugin,
    layerShinePlugin,
    maskLoadingPlugin,
} from '@meitu/saas-editor-core/es/framework';
import {
    backgroundLayerContextMenuPlugin,
    backgroundLayerPlugin,
    cropBackgroundPlugin,
    type BackgroundLayer,
    type CropBackground,
} from '@meitu/saas-editor-core/es/layers/background-layer';
import {
    brushDrawPlugin,
    brushLayerPlugin,
    type BrushDrawPlugin,
    type BrushLayer,
} from '@meitu/saas-editor-core/es/layers/brush-layer';
import {
    chartLayerPlugin,
    chartLayerShortcutMenuPlugin,
    type ChartLayer,
} from '@meitu/saas-editor-core/es/layers/chart-layer';
import {
    groupLayerContextMenuPlugin,
    groupLayerHotkeyPlugin,
    groupLayerPlugin,
    groupLayerShortcutMenuPlugin,
    type GroupLayer,
} from '@meitu/saas-editor-core/es/layers/group-layer';
import {
    imageEffectPlugin,
    imageFilterPlugin,
    imageLayerContextMenuPlugin,
    imageLayerPlugin,
    imageLayerShortcutMenuPlugin,
    type ImageFilterPlugin,
    type ImageLayer,
} from '@meitu/saas-editor-core/es/layers/image-layer';
import {
    cropImgPlugin,
    type CropImgPlugin,
} from '@meitu/saas-editor-core/es/layers/image-layer/plugins/apps/user-editor/crop-img';
import { dropImgPlugin } from '@meitu/saas-editor-core/es/layers/image-layer/plugins/apps/user-editor/drop-img';
import {
    layerContextMenuPlugin,
    layerHotkeyPlugin,
    layerShortcutMenuPlugin,
} from '@meitu/saas-editor-core/es/layers/layer';
import {
    intelligentColorPlugin,
    mainLayerPlugin,
    type IntelligentColorPlugin,
    type MainLayer,
} from '@meitu/saas-editor-core/es/layers/main-layer';
import {
    eliminatePenPlugin,
    type EliminatePenPlugin,
} from '@meitu/saas-editor-core/es/layers/main-layer/plugins/apps/user-editor/eliminate-pen';
import {
    imageEnhancePlugin,
    type ImageEnhancePlugin,
} from '@meitu/saas-editor-core/es/layers/main-layer/plugins/apps/user-editor/image-enhance';
import {
    markupDrawPlugin,
    markupLayerPlugin,
    type MarkupDrawPlugin,
    type MarkupLayer,
} from '@meitu/saas-editor-core/es/layers/markup-layer';
import { mediaLayerPlugin, type MediaLayer } from '@meitu/saas-editor-core/es/layers/media-layer';
import { qrCodeLayerPlugin, type QRCodeLayer } from '@meitu/saas-editor-core/es/layers/qr-code-layer';
import {
    tableLayerEditPlugin,
    tableLayerPlugin,
    type TableAction,
    type TableLayer,
} from '@meitu/saas-editor-core/es/layers/table-layer';
import {
    textLayerHotkeyPlugin,
    textLayerPlugin,
    textLayerShortcutMenuPlugin,
    textLayerSVGPreviewPlugin,
    type TextLayer,
} from '@meitu/saas-editor-core/es/layers/text-layer';
import {
    vectorLayerPlugin,
    vectorLayerShortcutMenuPlugin,
    vectorLayerSVGPreviewPlugin,
    type VectorLayer,
} from '@meitu/saas-editor-core/es/layers/vector-layer';
import { watermarkLayerPlugin, type WatermarkLayer } from '@meitu/saas-editor-core/es/layers/watermark-layer';
import {
    deformationImgPlugin,
    type DeformationImgPlugin,
} from '@meitu/saas-editor-core/es/plugins/apps/ai-photo-editor/deformation-img';
import {
    stageGridPlugin,
    type StageGridPlugin,
} from '@meitu/saas-editor-core/es/plugins/apps/ai-photo-editor/stage-grid';
import { cropStagePlugin, type CropStagePlugin } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/crop-stage';
import { dragLayerPlugin, type DragLayerPlugin } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/drag-layer';
import {
    imageCutoutPlugin,
    type ImageCutoutPlugin,
} from '@meitu/saas-editor-core/es/plugins/apps/user-editor/image-cutout';
import {
    referenceLinesPlugin,
    type ReferenceLines,
} from '@meitu/saas-editor-core/es/plugins/apps/user-editor/reference-lines';
import { rulerBarPlugin, type RulerBar } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/ruler-bar';
import { stageHoverPlugin } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/stage-hover';
import { stageOperatePlugin } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/stage-operate';
import {
    clipboardContextMenuPlugin,
    clipboardHotkeyPlugin,
    clipboardPlugin,
    type ClipboardPlugin,
} from '@meitu/saas-editor-core/es/plugins/clipboard';
import { contextMenuPlugin, type ContextMenuPlugin } from '@meitu/saas-editor-core/es/plugins/context-menu';
import { displacementPlugin } from '@meitu/saas-editor-core/es/plugins/displacement';
import { mouseOperatePlugin } from '@meitu/saas-editor-core/es/plugins/mouse-operate';
import {
    getBuiltinShortcutMenuItems,
    shortcutMenuPlugin,
    type ShortcutMenu,
} from '@meitu/saas-editor-core/es/plugins/shortcut-menu';
import { smartGuidesPlugin } from '@meitu/saas-editor-core/es/plugins/smart-guides';
import { stageToolbarPlugin, type StageToolbar } from '@meitu/saas-editor-core/es/plugins/stage-toolbar';
import { textEditorPlugin, type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { viewportScrollBarPlugin } from '@meitu/saas-editor-core/es/plugins/viewport-scroll-bar';
import { PUBLIC_URL } from './react-process-env';

/** designer-editor 使用的编辑器实例及业务插件类型 */
export interface IEditor extends Editor {
    download: Download;
    template: Template;
    wasm: Wasm;
    dragLayer: DragLayerPlugin;
    tableAction: TableAction;
    brushDraw: BrushDrawPlugin;
    clipboard: ClipboardPlugin;
    contextMenu: ContextMenuPlugin;
    cropBackground: CropBackground;
    cropImg: CropImgPlugin;
    cropStage: CropStagePlugin;
    deformationImg: DeformationImgPlugin;
    eliminatePen: EliminatePenPlugin;
    imageCutout: ImageCutoutPlugin;
    imageEnhance: ImageEnhancePlugin;
    imageFilter: ImageFilterPlugin;
    intelligentColor: IntelligentColorPlugin;
    markupDraw: MarkupDrawPlugin;
    referenceLines: ReferenceLines;
    rulerBar: RulerBar;
    stageGrid: StageGridPlugin;
    textEdit: TextEditorPlugin;
    stageToolbar: StageToolbar;
    shortcutMenu: ShortcutMenu;
    backgroundLayer: BackgroundLayer;
    brushLayer: BrushLayer;
    chartLayer: ChartLayer;
    groupLayer: GroupLayer;
    imageLayer: ImageLayer;
    mainLayer: MainLayer;
    markupLayer: MarkupLayer;
    mediaLayer: MediaLayer;
    qrCodeLayer: QRCodeLayer;
    tableLayer: TableLayer;
    textLayer: TextLayer;
    vectorLayer: VectorLayer;
    watermarkLayer: WatermarkLayer;
}

/** designer-editor 全局编辑器实例 */
export const editor = new Editor('editor-container', {
    PUBLIC_URL,
    padding: 35,
}) as IEditor;

editor.registerPlugins([
    { plugin: viewportScrollBarPlugin },
    { plugin: backgroundLayerPlugin },
    { plugin: brushLayerPlugin },
    { plugin: chartLayerPlugin },
    { plugin: groupLayerPlugin },
    { plugin: imageLayerPlugin },
    { plugin: imageEffectPlugin },
    { plugin: mainLayerPlugin },
    { plugin: markupLayerPlugin },
    { plugin: mediaLayerPlugin },
    { plugin: qrCodeLayerPlugin },
    { plugin: tableLayerPlugin },
    { plugin: textLayerPlugin },
    { plugin: vectorLayerPlugin },
    { plugin: watermarkLayerPlugin },
    { plugin: downloadExtension },
    { plugin: templateExtension },
    { plugin: wasmExtension },
    { plugin: brushDrawPlugin },
    { plugin: clipboardPlugin },
    { plugin: cropBackgroundPlugin },
    { plugin: cropImgPlugin },
    { plugin: dropImgPlugin },
    { plugin: cropStagePlugin },
    { plugin: deformationImgPlugin },
    { plugin: displacementPlugin },
    { plugin: dragLayerPlugin },
    { plugin: eliminatePenPlugin },
    { plugin: imageCutoutPlugin },
    { plugin: imageEnhancePlugin },
    { plugin: imageFilterPlugin },
    { plugin: intelligentColorPlugin },
    { plugin: layerLoadingPlugin },
    { plugin: layerShinePlugin },
    { plugin: markupDrawPlugin },
    { plugin: maskLoadingPlugin },
    { plugin: mouseOperatePlugin },
    { plugin: referenceLinesPlugin },
    { plugin: rulerBarPlugin },
    { plugin: smartGuidesPlugin },
    { plugin: stageGridPlugin },
    { plugin: stageHoverPlugin },
    { plugin: stageOperatePlugin },
    { plugin: stageToolbarPlugin },
    { plugin: tableLayerEditPlugin },
    { plugin: textEditorPlugin },
    { plugin: textLayerSVGPreviewPlugin },
    { plugin: vectorLayerSVGPreviewPlugin },
    { plugin: frameworkHotkeyPlugin },
    { plugin: clipboardHotkeyPlugin },
    { plugin: layerHotkeyPlugin },
    { plugin: groupLayerHotkeyPlugin },
    { plugin: textLayerHotkeyPlugin },
    { plugin: contextMenuPlugin },
    { plugin: clipboardContextMenuPlugin },
    { plugin: backgroundLayerContextMenuPlugin },
    { plugin: layerContextMenuPlugin },
    { plugin: groupLayerContextMenuPlugin },
    { plugin: imageLayerContextMenuPlugin },
    { plugin: shortcutMenuPlugin },
    { plugin: layerShortcutMenuPlugin },
    { plugin: groupLayerShortcutMenuPlugin },
    { plugin: imageLayerShortcutMenuPlugin },
    { plugin: textLayerShortcutMenuPlugin },
    { plugin: vectorLayerShortcutMenuPlugin },
    { plugin: chartLayerShortcutMenuPlugin },
]);

editor.getInstance<StageToolbar>('StageToolbar').setConfig({
    appearance: 'standard',
    position: 'right',
    items: {
        referenceLines: true,
        hierarchy: { mode: 'panel' },
        scale: { mode: 'menu', showCanvasSize: true },
        fitViewport: false,
        customNode: true,
    },
});
editor
    .getInstance<ShortcutMenu>('ShortcutMenu')
    .setDefaultMenuEnabled(true)
    // 设计师编辑器未提供 AI 花字面板，移除对应快捷入口，保留图片 AI 操作
    .setItems(getBuiltinShortcutMenuItems().filter((item) => item.key !== 'ai-text-item-20'));

editor.registerInstance({
    download: editor.getInstance<Download>('Download'),
    template: editor.getInstance<Template>('Template'),
    wasm: editor.getInstance<Wasm>('Wasm'),
    dragLayer: editor.getInstance<DragLayerPlugin>('DragLayerPlugin'),
    tableAction: editor.getInstance<TableAction>('TableAction'),
    brushDraw: editor.getInstance<BrushDrawPlugin>('BrushDrawPlugin'),
    clipboard: editor.getInstance<ClipboardPlugin>('ClipboardPlugin'),
    contextMenu: editor.getInstance<ContextMenuPlugin>('ContextMenuPlugin'),
    cropBackground: editor.getInstance<CropBackground>('CropBackground'),
    cropImg: editor.getInstance<CropImgPlugin>('CropImgPlugin'),
    cropStage: editor.getInstance<CropStagePlugin>('CropStagePlugin'),
    deformationImg: editor.getInstance<DeformationImgPlugin>('DeformationImgPlugin'),
    eliminatePen: editor.getInstance<EliminatePenPlugin>('EliminatePenPlugin'),
    imageCutout: editor.getInstance<ImageCutoutPlugin>('ImageCutoutPlugin'),
    imageEnhance: editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin'),
    imageFilter: editor.getInstance<ImageFilterPlugin>('ImageFilterPlugin'),
    intelligentColor: editor.getInstance<IntelligentColorPlugin>('IntelligentColorPlugin'),
    markupDraw: editor.getInstance<MarkupDrawPlugin>('MarkupDrawPlugin'),
    referenceLines: editor.getInstance<ReferenceLines>('ReferenceLines'),
    rulerBar: editor.getInstance<RulerBar>('RulerBar'),
    stageGrid: editor.getInstance<StageGridPlugin>('StageGridPlugin'),
    textEdit: editor.getInstance<TextEditorPlugin>('TextEditorPlugin'),
    stageToolbar: editor.getInstance<StageToolbar>('StageToolbar'),
    shortcutMenu: editor.getInstance<ShortcutMenu>('ShortcutMenu'),
    backgroundLayer: editor.getInstance<BackgroundLayer>('BackgroundLayer'),
    brushLayer: editor.getInstance<BrushLayer>('BrushLayer'),
    chartLayer: editor.getInstance<ChartLayer>('ChartLayer'),
    groupLayer: editor.getInstance<GroupLayer>('GroupLayer'),
    imageLayer: editor.getInstance<ImageLayer>('ImageLayer'),
    mainLayer: editor.getInstance<MainLayer>('MainLayer'),
    markupLayer: editor.getInstance<MarkupLayer>('MarkupLayer'),
    mediaLayer: editor.getInstance<MediaLayer>('MediaLayer'),
    qrCodeLayer: editor.getInstance<QRCodeLayer>('QRCodeLayer'),
    tableLayer: editor.getInstance<TableLayer>('TableLayer'),
    textLayer: editor.getInstance<TextLayer>('TextLayer'),
    vectorLayer: editor.getInstance<VectorLayer>('VectorLayer'),
    watermarkLayer: editor.getInstance<WatermarkLayer>('WatermarkLayer'),
});

if (process.env.NODE_ENV !== 'release') {
    // @ts-expect-error 开发环境挂载编辑器实例用于调试
    window.editor = editor;
}
